import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import SpiningLoader from '@/components/common/SpinningLoader';
import type {AfriexFeature} from '@/types';
import * as formatDate from '@/utils/date';

import FeatureUpdateButton from './FeatureUpdateButton';

type FeatureSummaryProp = {
  item: AfriexFeature;
  isLoading?: boolean;
  isDetail?: boolean;
};

const FeatureSummary = ({item, isLoading}: FeatureSummaryProp) => {
  return isLoading ? (
    <SpiningLoader />
  ) : (
    <div
      data-cy="merchant-features-summary"
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">{item.name}</h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Feature ID
          </p>
          <p
            data-cy="merchant-features-summary-id"
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {item?.id}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Name</p>
          <p
            data-cy="merchant-features-summary-name"
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {item?.name}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Code</p>
          <p
            data-cy="merchant-features-summary-code"
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {item?.code}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Feature Status
          </p>
          <p
            data-cy="merchant-features-summary-status"
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {!item?.meta?.flags?.isDeactivated ? (
              <span className="text-green-500">Active</span>
            ) : (
              <span className="text-red-500">Inactive</span>
            )}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p
            data-cy="merchant-features-summary-date-created"
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
          >
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          <PermissionsProvider permission={AfriexAdminPermission.FeatureManage}>
            <FeatureUpdateButton
              initialData={item}
              dataCy="merchant-features-summary-update"
            />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default FeatureSummary;
